/**
 * Tekst en werkbalk van de mailstand: een opmaakeditor (TipTap, sessie 70
 * blok 3). Alleen hier; chat en WhatsApp blijven platte tekst.
 *
 * Wat mag: alinea, regel, vet, cursief, onderstreept,
 * opsomming en link. Geen koppen, citaten, code, doorhalen, genummerde
 * lijst of afbeelding in de tekst; geplakte opmaak buiten die lijst valt
 * terug naar gewone tekst. Een afbeelding gaat als bijlage (de knop in de
 * werkbalk opent de bestaande bijlagekiezer).
 *
 * Enter geeft een nieuwe regel, twee keer Enter een nieuwe alinea, zoals
 * bij platte tekst (een lege regel is een alinea). Het iPhone-toetsenbord
 * heeft geen Shift+Enter (recensie blok 3). In een lijst maakt Enter een
 * nieuw punt, zoals altijd.
 *
 * De waarde naar buiten blijft een string: HTML uit de editor, of "" als
 * het vak leeg is (zodat "staat er tekst" overal hetzelfde blijft werken).
 * Binnenkomende platte tekst (motorconcept, copiloot, oud opgeslagen vak)
 * wordt eerst alinea's en regels, met de regels van `mail_opmaak.naar_html`.
 * Wordt de waarde van buiten vervangen (copiloot, leeg na versturen), dan
 * laadt de editor hem opnieuw; de eigen uitvoer tijdens het typen komt
 * niet terug in de editor, dus de cursor blijft staan.
 */
import { EditorContent, Extension, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef, type ReactNode } from "react";

import { naarEditor, uitEditor } from "./mailopmaak";
import { MARKEER_SLEUTEL, Markeer } from "./markeer";
import { MailWerkbalk } from "./werkbalk";
import "./mailtekst.css";

const ALLEEN_WEB_OF_MAIL = /^(https?:\/\/|mailto:)/i;

/** Enter = regel, Enter op een lege regel = nieuwe alinea (buiten lijsten). */
const ENTER_ALS_REGEL = Extension.create({
  name: "enterAlsRegel",
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { $from, empty } = editor.state.selection;
        if (!empty || editor.isActive("listItem")) return false;
        if ($from.parent.content.size === 0) return false;
        const ervoor = $from.nodeBefore;
        if (ervoor && ervoor.type.name === "hardBreak") {
          // Tweede Enter: de regelovergang weg, een nieuwe alinea erin.
          return editor
            .chain()
            .command(({ tr }) => {
              tr.delete($from.pos - ervoor.nodeSize, $from.pos);
              return true;
            })
            .splitBlock()
            .run();
        }
        return editor.commands.setHardBreak();
      },
    };
  },
});

/** De editor-uitbreidingen van het mailvak. */
const MAIL_EXTENSIES = [
  ENTER_ALS_REGEL,
  StarterKit.configure({
    heading: false,
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    orderedList: false,
    link: {
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      defaultProtocol: "https",
      isAllowedUri: (url) => ALLEEN_WEB_OF_MAIL.test(url.trim()),
      // Kale <a href> in de mail: geen target, rel of class.
      HTMLAttributes: { target: null, rel: null, class: null },
    },
  }),
  Markeer,
];

const GEEN_ZINNEN: readonly string[] = [];

export function MailTekst({
  waarde,
  onWaarde,
  onAfbeelding,
  label = "Antwoord",
  markeer = GEEN_ZINNEN,
  onder = null,
}: {
  waarde: string;
  onWaarde: (tekst: string) => void;
  onAfbeelding: () => void;
  /** Naam voor schermlezers; Beslissen noemt het "Bericht bij akkoord". */
  label?: string;
  /** Motor 7 (keuze 9 van 75): zinnen uit de lijsten die een lijntje krijgen. */
  markeer?: readonly string[];
  /** Direct onder de tekst, boven de werkbalk (de handtekening, keuze 9). */
  onder?: ReactNode;
}) {
  // De laatste waarde die de editor zelf naar buiten gaf, of die hij als
  // laatste van buiten laadde. Alleen een andere waarde laadt opnieuw.
  const laatst = useRef(waarde);
  const onWaardeRef = useRef(onWaarde);
  useEffect(() => {
    onWaardeRef.current = onWaarde;
  });

  const editor = useEditor({
    extensions: MAIL_EXTENSIES,
    content: naarEditor(waarde),
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        class: "tekst mailtekst-vlak",
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": label,
        "aria-placeholder": "Typ je antwoord",
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = e.getText().trim() ? uitEditor(e.getHTML()) : "";
      laatst.current = html;
      onWaardeRef.current(html);
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (waarde === laatst.current) return;
    laatst.current = waarde;
    editor.commands.setContent(naarEditor(waarde), { emitUpdate: false });
  }, [waarde, editor]);

  // De lijstzinnen: opslaan in de uitbreiding en de decoraties opnieuw laten rekenen.
  const markeerSleutel = markeer.join("\u0000");
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const opslag = (editor.storage as unknown as Record<string, { zinnen: readonly string[] }>)
      .m7Markeer;
    if (!opslag) return;
    opslag.zinnen = markeerSleutel ? markeerSleutel.split("\u0000") : [];
    editor.view.dispatch(editor.state.tr.setMeta(MARKEER_SLEUTEL, true));
  }, [editor, markeerSleutel]);

  const leeg = useEditorState({
    editor,
    selector: ({ editor: e }) => (e ? e.isEmpty : true),
  });

  return (
    <>
      <div className="lijf mailtekst" data-leeg={leeg ? "ja" : undefined}>
        <EditorContent editor={editor} />
      </div>
      {onder}
      {editor && <MailWerkbalk editor={editor} onAfbeelding={onAfbeelding} />}
    </>
  );
}
